import { CategoryId } from "./prebuiltAiTabConfig";

export interface UseCase {
  title: string;
  body: string;
}

export interface Benefit {
  id: string;
  label: string;
}

export interface AgentContent {
  agentName: string;
  description: string;
  useCase: UseCase;
  benefits: Benefit[];
}

export type AgentContentData = Record<CategoryId, AgentContent>;

export const PREBUILT_AGENTS_CONTENT: AgentContentData = {
  project: {
    agentName: "The Strategic Alignment Agent",
    description:
      "Your Project Agent eliminates manual oversight by instantly generating structured project outlines, identifying cross-project risks, and balancing team capacity across the entire portfolio.",
    useCase: {
      title: "Real-world Use Case:",
      body: "Analyze the attached RFP document and create a project board with a structured list of tasks and subtasks. Based on these tasks, identify potential resource bottlenecks and suggest a workload distribution plan for a team of 5.",
    },
    benefits: [
      { id: "instant-outlines", label: "Instant Project Outlines" },
      { id: "risk-assessment", label: "Automated Risk Assessment" },
      { id: "workload-planning", label: "Predictive Workload Planning" },
    ],
  },
  product: {
    agentName: "The Roadmap Architect",
    description:
      "Go from idea to PRD in seconds. The Product Agent analyzes user feedback and competitive research to build detailed product specs and roadmaps.",
    useCase: {
      title: "Real-world Use Case:",
      body: "Analyze the uploaded transcript from our product brainstorming session. Generate a 10-page PRD (Product Requirement Document) that includes user personas, functional requirements, and a prioritized feature backlog. Map these requirements directly to our Product Roadmap for the next quarter.",
    },
    benefits: [
      { id: "prd-generator", label: "One-Click PRD Generator" },
      { id: "prioritization", label: "Prioritization AI" },
      { id: "interactive-roadmaps", label: "Interactive Roadmaps" },
    ],
  },
  engineering: {
    agentName: "The Sprint Master Agent",
    description:
      "Your Engineering Agent doesn't just list tasks. It reads your GitHub repos, generates technical specifications, and automatically highlights risks in your sprint roadmap.",
    useCase: {
      title: "Real-world Use Case:",
      body: "Analyze the merged pull request history from our integrated GitHub/GitLab repositories and identify any tasks that exceeded their original estimates. Generate a Sprint Retrospective summary that highlights team velocity, lists specific technical risks encountered, and suggests three actionable improvements for the next sprint cycle.",
    },
    benefits: [
      { id: "sync-repos", label: "Sync Repos instantly" },
      { id: "api-docs", label: "AI-Generated API Docs" },
      { id: "bug-risk", label: "Bug Risk Prediction" },
    ],
  },
  qa: {
    agentName: "The Precision Testing Agent",
    description:
      "Your QA Agent streamlines the release cycle by automating test execution plans, tracking bug dependencies, and summarizing sprint performance to ensure zero-defect delivery.",
    useCase: {
      title: "Real-world Use Case:",
      body: "Summarize the project status for our current testing sprint. Identify all critical unresolved bugs and high-impact risks. Generate a Sprint Retrospective summary highlighting key areas for improvement before we move to production.",
    },
    benefits: [
      { id: "test-strategies", label: "Automated Test Strategies" },
      { id: "retrospective-insights", label: "Sprint Retrospective Insights" },
      { id: "dependency-tracking", label: "Dependency Tracking" },
    ],
  },
  marketing: {
    agentName: "The Brand Growth Agent",
    description:
      "Stop starting from a blank page. Your Marketing Agent creates multi-channel campaign ideas, drafts SEO-optimized copy, and manages creative assets in one view.",
    useCase: {
      title: "Real-world Use Case:",
      body: "Using the attached product brief, create a 4-week multi-channel marketing campaign. Draft five SEO-optimized blog topics, a three-part email sequence for lead nurturing, and ten social media captions. Organize these deliverables into a Marketing Timeline view to track production deadlines.",
    },
    benefits: [
      { id: "seo-content", label: "SEO Content Engine" },
      { id: "campaign-planner", label: "Campaign Planner" },
      { id: "asset-tracker", label: "Asset Lifecycle Tracker" },
    ],
  },
  sales: {
    agentName: "The Pipeline Velocity Agent",
    description:
      "Your Sales Agent accelerates the deal cycle by analyzing vendor bids, generating professional proposals, and automating the management of client information and billing.",
    useCase: {
      title: "Real-world Use Case:",
      body: "Analyze these three vendor bid documents side-by-side. Create a comparison table focused on accuracy, pricing, and alignment with our core RFP requirements. Based on this, draft a professional proposal to present to the selection committee.",
    },
    benefits: [
      { id: "grant-proposal", label: "Grant & Proposal Writer" },
      { id: "rfp-analyzer", label: "RFP Analyzer" },
      { id: "client-management", label: "Unified Client Management" },
    ],
  },
  operations: {
    agentName: "The Efficiency Engine Agent",
    description:
      "Your Operations Agent transforms business processes by tracking performance data in real-time, automating complex workflow transitions, and managing the entire lifecycle from planning to release.",
    useCase: {
      title: "Real-world Use Case:",
      body: "Review my current supply chain project data. Track and analyze performance data to identify at least three major bottlenecks in our workflow. Provide a targeted improvement plan and a custom dashboard layout to monitor these metrics in real-time.",
    },
    benefits: [
      {
        id: "performance-analytics",
        label: "Intelligent Performance Analytics",
      },
      { id: "sop-digitalization", label: "SOP Digitalization" },
      { id: "workflow-continuity", label: "24/7 Workflow Continuity" },
    ],
  },
  it: {
    agentName: "The Service Desk Autopilot",
    description:
      "Your IT Support Agent provides round-the-clock resolution by analyzing inquiry patterns, automating ticket triage, and generating instant technical documentation for troubleshooting.",
    useCase: {
      title: "Real-world Use Case:",
      body: "I am uploading our 'IT Support SOP' and 'Troubleshooting Manual'. Using these documents, act as an AI Support Agent to triage incoming ticket descriptions, categorize them by priority, and provide instant step-by-step resolution instructions.",
    },
    benefits: [
      { id: "smart-triage", label: "Smart Ticket Triage" },
      { id: "kb-synthesizer", label: "Knowledge Base Synthesizer" },
      { id: "service-level", label: "Service Level Management" },
    ],
  },
  hr: {
    agentName: "The People Success Agent",
    description:
      "Your HR Agent revolutionizes talent management by automating candidate engagement, streamlining personalized onboarding schedules, and aligning individual performance with data-driven OKRs.",
    useCase: {
      title: "Real-world Use Case:",
      body: "Generate an onboarding project for a new [Job Title] hire. Create a 30-60-90 day task list including personalized training schedules, orientation milestones, and automated goal setting based on our standard OKR framework.",
    },
    benefits: [
      { id: "onboarding-hub", label: "Automated Onboarding Hub" },
      { id: "okr-setting", label: "Data-Driven OKR Setting" },
      { id: "compliance-specialist", label: "Policy & Compliance Specialist" },
    ],
  },
  education: {
    agentName: "The District Leadership Agent",
    description:
      "Your Education Agent empowers administrators by auto-generating strategic plans, analyzing student performance trends, and ensuring district-wide compliance with FERPA and Title IX standards.",
    useCase: {
      title: "Real-world Use Case:",
      body: "Using the uploaded student performance metrics and state policy documents, generate a strategic district plan. Ensure the plan is FERPA compliant and includes measurable OKRs for improving attendance and grade patterns across all schools.",
    },
    benefits: [
      { id: "strategic-plan", label: "Strategic Plan Generator" },
      { id: "compliance-specialist", label: "Compliance Specialist" },
      { id: "student-success", label: "Student Success Analyst" },
    ],
  },
};
